// src/pages/cliente/ClientesPage.jsx
import React, { useState, useEffect } from 'react';
import { get, del } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import useAuthStore from '../../store/authStore';
import ClientesSearchAndCreate from '../../components/clients/ClientesSearchAndCreate';
import ClientesTable from '../../components/clients/ClientesTable';
import CrearClienteModal from '../../components/clients/CrearClienteModal';
import CustomerSegmentationFilter from '../../components/clients/CustomerSegmentationFilter';
import { useNavigate } from 'react-router-dom';
import { useClientesConRFM } from '../../hooks/useClientesConRFM';

function ClientesPage() {
    const { user } = useAuthStore();
    const [filteredClientes, setFilteredClientes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    // Opciones por defecto (estándar) con colores fijos
    const [tagOptions, setTagOptions] = useState([
        { value: 'VIP', label: 'VIP', color: 'green' },
        { value: 'Frecuentes', label: 'Frecuentes', color: 'orange' },
        { value: 'Inactivos', label: 'Inactivos', color: 'red' },
    ]);
    const navigate = useNavigate();

    // Usar hook personalizado para obtener clientes con RFM
    const { clientes, loading, error } = useClientesConRFM(12);

    // Función para obtener clientes; recibe un parámetro opcional "tag"
    // Nota: Cuando se filtra por tag, no se incluyen datos RFM (se usa endpoint diferente)
    const fetchClientes = async (tag = 'All') => {
        try {
            if (tag === 'All') {
                // Si es 'All', usar el hook que ya tiene RFM
                // No hacer nada aquí, el hook ya maneja esto
                // El useEffect ya actualiza filteredClientes cuando cambian los clientes
                return;
            } else {
                // Filtro básico usando el endpoint existente (sin RFM)
                const response = await get(`clientes/filter-by-tag?tag=${tag}`);
                setFilteredClientes(response);
                updateTagOptions(response);
            }
        } catch (error) {
            console.error('Error fetching clients:', error);
            Swal.fire({
                title: 'Error',
                text: error.message,
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        }
    };

    // Extrae etiquetas únicas de los clientes y las fusiona con las opciones predeterminadas
    const updateTagOptions = (clientesData) => {
        const tagsSet = new Set();
        clientesData.forEach((cliente) => {
            if (cliente.segments && Array.isArray(cliente.segments)) {
                cliente.segments.forEach((tag) => {
                    tagsSet.add(typeof tag === 'object' ? tag.value : tag);
                });
            }
        });
        const defaultOptions = [
            { value: 'VIP', label: 'VIP', color: 'green' },
            { value: 'Frecuentes', label: 'Frecuentes', color: 'orange' },
            { value: 'Inactivos', label: 'Inactivos', color: 'red' },
        ];
        const customOptions = Array.from(tagsSet).map((tag) => ({ value: tag, label: tag }));
        const mergedOptions = [...defaultOptions];
        customOptions.forEach((opt) => {
            if (!mergedOptions.some((o) => o.value === opt.value)) {
                mergedOptions.push(opt);
            }
        });
        setTagOptions(mergedOptions);
    };

    // Actualizar clientes filtrados cuando cambian los clientes del hook
    useEffect(() => {
        if (clientes && clientes.length > 0) {
            setFilteredClientes(clientes);
            updateTagOptions(clientes);
        }
    }, [clientes]);

    // Mostrar error si hay
    useEffect(() => {
        if (error) {
            Swal.fire({
                title: 'Error',
                text: error.message || 'Error al cargar los clientes',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        }
    }, [error]);

    // Recibe filtros (cadena con etiquetas separadas por comas)
    const handleSegmentFilter = (tag) => {
        fetchClientes(tag);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        const lowerTerm = term.toLowerCase();
        const results = (clientes || []).filter(
            (cliente) =>
                (cliente.nombre && cliente.nombre.toLowerCase().includes(lowerTerm)) ||
                (cliente.email && cliente.email.toLowerCase().includes(lowerTerm)) ||
                (cliente.telefono && cliente.telefono.toString().toLowerCase().includes(lowerTerm))
        );
        setFilteredClientes(results);
    };

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    if (loading) {
        return (
            <div className="w-full min-w-0 overflow-x-hidden">
                <div className="container mx-auto px-4">
                    <div className="bg-white p-4 md:p-6 rounded-lg shadow-md">
                        <div className="text-center py-8">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            <p className="mt-4 text-gray-600">Cargando clientes con datos RFM...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        // Se agrega overflow-x-hidden al contenedor principal para evitar scroll horizontal
        <div className="w-full min-w-0 overflow-x-hidden">
            <div className="container mx-auto px-4">
                <div className="bg-white p-4 md:p-6 rounded-lg shadow-md">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                        <div>
                            <h1 className="text-2xl md:text-4xl font-bold mb-2">Clientes</h1>
                            <p className="text-lg md:text-xl">Aquí puedes gestionar tus clientes.</p>
                        </div>
                        <div className="mt-4 md:mt-0">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0">
                                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                                            <span className="text-white font-bold text-sm">{clientes?.length || 0}</span>
                                        </div>
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-medium text-blue-900">Total de Clientes</p>
                                        <p className="text-xs text-blue-700">
                                            {filteredClientes.length === (clientes?.length || 0)
                                                ? 'Todos los clientes'
                                                : `${filteredClientes.length} de ${clientes?.length || 0} mostrados`
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Filtro de segmentación */}
                    <CustomerSegmentationFilter onFilter={handleSegmentFilter} options={tagOptions} />

                    {/* Buscador y botón para crear */}
                    <ClientesSearchAndCreate
                        onCreate={openModal}
                        searchTerm={searchTerm}
                        setSearchTerm={handleSearch}
                    />

                    {/* Tabla de clientes con scroll horizontal controlado */}
                    <div className="overflow-x-auto">
                        <ClientesTable
                            clientes={filteredClientes}
                            onDelete={async (id) => {
                                const result = await Swal.fire({
                                    title: '¿Estás seguro?',
                                    text: 'Esta acción no se puede deshacer',
                                    icon: 'warning',
                                    showCancelButton: true,
                                    confirmButtonText: 'Sí, eliminar',
                                    cancelButtonText: 'Cancelar',
                                });
                                if (result.isConfirmed) {
                                    await del(`clientes/${id}`);
                                    fetchClientes('All');
                                    Swal.fire({
                                        title: 'Eliminado',
                                        text: 'El cliente ha sido eliminado.',
                                        icon: 'success',
                                        confirmButtonText: 'Aceptar',
                                    });
                                }
                            }}
                            fetchClientes={() => fetchClientes('All')}
                        />
                    </div>

                    {/* Modal de creación de cliente */}
                    <CrearClienteModal
                        isOpen={isModalOpen}
                        onClose={closeModal}
                        onClienteCreated={() => fetchClientes('All')}
                    />
                </div>
            </div>
        </div>
    );
}

export default ClientesPage;
